import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

const EMPRESAS_DISPONIBLES = (process.env.REACT_APP_EMPRESAS || 'CIMSA,CODYLSA,DYLASA,TDU,MALARGUE').split(',');
const CURRENT_EMPRESA = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA'; // La base donde reside la VC de SAP

const TabStatusAutorizacion = () => {
    const [docs, setDocs] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openRow, setOpenRow] = useState(null);
    const [details, setDetails] = useState({});

    // --- ESTADOS DE FILTROS FRONT-END ---
    const [filterEmpresa, setFilterEmpresa] = useState('TODAS'); 
    const [filterUsuario, setFilterUsuario] = useState('TODOS');
    const [searchNum, setSearchNum] = useState('');

    const formatMoney = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val || 0);

    // Se ejecuta UNA SOLA VEZ al cargar el componente
    useEffect(() => {
        const controller = new AbortController();
        fetchDocs(controller.signal);
        return () => controller.abort();
    }, []);

    const fetchDocs = async (signal) => {
        setLoading(true);
        setError(null);
        try {
            // Siempre consultamos a CIMSA porque ahí está la vista
            const { data } = await api.get('/compras/dashboard/status-autorizacion', {
                params: { empresa: CURRENT_EMPRESA },
                signal,
            });

            if (Array.isArray(data) && data.length > 0) {
                // 1. FILTRAMOS SOLO "Orden de Compra"
                const soloOC = data.filter(item => item.TipodeDocumento === 'Orden de Compra');

                // 2. AGRUPAMOS LA DATA
                const grouped = soloOC.reduce((acc, item) => {
                    if (!item.Ndeborrador) return acc;
                    const key = `${item.Ndeborrador}-${item.TipodeDocumento}`;
                    
                    if (!acc[key]) {
                        acc[key] = {
                            ...item,
                            firmas: []
                        };
                    }
                    
                    if (item.Aprobador || item.Etapa) {
                         acc[key].firmas.push({
                            Etapa: item.Etapa || 'Sin Etapa',
                            Aprobador: item.Aprobador || 'Sin Aprobador',
                            EstadoEtapa: item.EstadodeEtapa || 'Desconocido'
                        });
                    }
                    return acc;
                }, {});

                setDocs(Object.values(grouped));
            } else {
                setDocs([]);
            }
        } catch (err) {
            if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
            console.error(err);
            setError("Error cargando status de autorización desde SAP.");
            setDocs([]);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    const toggleRow = async (doc) => {
        const id = `${doc.Ndeborrador}-${doc.TipodeDocumento}`;
        if (openRow === id) {
            setOpenRow(null);
            return;
        }
        setOpenRow(id);

        if (!details[id]) {
            try {
                // Para traer el detalle del borrador, usamos la empresa del documento
                // o por defecto CIMSA si no viniera
                const dbObjetivo = doc.Empresa || CURRENT_EMPRESA;
                const { data } = await api.get(`/compras/dashboard/detalle-borrador/${doc.Ndeborrador}`, {
                    params: { tipoDoc: doc.TipodeDocumento, empresa: dbObjetivo },
                });
                setDetails(prev => ({ ...prev, [id]: data }));
            } catch (err) {
                console.error("Error cargando detalle:", err);
            }
        }
    };

    // --- LISTA ÚNICA DE USUARIOS ---
    const uniqueUsuarios = useMemo(() => {
        const users = docs.map(d => d.U_NAME).filter(Boolean); 
        return [...new Set(users)].sort(); 
    }, [docs]);

    // --- LÓGICA DE FILTRADO FRONT-END ---
    const displayedDocs = docs.filter(doc => {
        // Filtrar por Empresa
        const matchEmpresa = filterEmpresa === 'TODAS' || doc.Empresa === filterEmpresa;
        // Filtrar por Usuario
        const matchUser = filterUsuario === 'TODOS' || doc.U_NAME === filterUsuario;
        // Filtrar por Número
        const matchNum = searchNum === '' || doc.Ndeborrador.toString().includes(searchNum);
        
        return matchEmpresa && matchUser && matchNum;
    });

    return (
        <div className="mt-4 px-3">
            <h4 className="mb-3 text-primary fw-bold"><i className="bi bi-shield-check me-2"></i>Status de Autorización</h4>
            
            {/* --- BARRA DE FILTROS SUPERIOR --- */}
            <div className="card shadow-sm mb-4 border-0 bg-light">
                <div className="card-body py-3">
                    <div className="row g-3 align-items-end">
                        
                        {/* Filtro Empresa */}
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-muted mb-1">Empresa SAP</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-building"></i></span>
                                <select 
                                    className="form-select border-start-0" 
                                    value={filterEmpresa} 
                                    onChange={(e) => setFilterEmpresa(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="TODAS">Todas las empresas</option>
                                    {EMPRESAS_DISPONIBLES.map(emp => (
                                        <option key={emp} value={emp}>{emp}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Filtro Usuario Solicitante */}
                        <div className="col-md-4">
                            <label className="form-label small fw-bold text-muted mb-1">Usuario Solicitante</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-person"></i></span>
                                <select 
                                    className="form-select border-start-0" 
                                    value={filterUsuario} 
                                    onChange={(e) => setFilterUsuario(e.target.value)}
                                    disabled={loading || uniqueUsuarios.length === 0}
                                >
                                    <option value="TODOS">Todos los usuarios</option>
                                    {uniqueUsuarios.map(u => (
                                        <option key={u} value={u}>{u}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Filtro Número OC */}
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-muted mb-1">N° Borrador / OC</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                                <input 
                                    type="text" 
                                    className="form-control border-start-0" 
                                    placeholder="Buscar número..." 
                                    value={searchNum}
                                    onChange={(e) => setSearchNum(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        {/* Contador de Resultados */}
                        <div className="col-md-2 text-end">
                            <span className="badge bg-secondary p-2 w-100">
                                {displayedDocs.length} Registros
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MANEJO DE ESTADOS Y LISTA --- */}
            {loading ? (
                <div className="text-center p-5"><div className="spinner-border text-primary"></div><p className="mt-2 text-primary">Conectando con SAP...</p></div>
            ) : error ? (
                <div className="alert alert-danger shadow-sm">{error}</div>
            ) : displayedDocs.length === 0 ? (
                 <div className="alert alert-info shadow-sm">No se encontraron Órdenes de Compra pendientes para estos filtros.</div>
            ) : (
                <div className="accordion pb-5">
                    {displayedDocs.map((doc, idx) => {
                        const id = `${doc.Ndeborrador}-${doc.TipodeDocumento}`;
                        const isOpen = openRow === id;
                        const detail = details[id];

                        return (
                            <div className="accordion-item mb-3 shadow-sm border rounded" key={id}>
                                <h2 className="accordion-header">
                                    <button className={`accordion-button ${isOpen ? '' : 'collapsed'}`} onClick={() => toggleRow(doc)} type="button">
                                        <div className="d-flex w-100 justify-content-between align-items-center pe-3">
                                            
                                            <div className="d-flex align-items-center">
                                                <span className="badge bg-primary me-2">{doc.TipodeDocumento}</span>
                                                <strong className="text-dark me-3"># {doc.Ndeborrador}</strong>
                                                <span className="text-muted small">Solicita: {doc.U_NAME || 'Desconocido'}</span>
                                            </div>

                                            <div className="flex-grow-1 text-center">
                                                {doc.Empresa && (
                                                    <span className="badge bg-light text-secondary border px-3 py-1">
                                                        <i className="bi bi-building me-1"></i> {doc.Empresa}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="text-end">
                                                <span className={`badge ${doc.EstadodeDocumento === 'Pendiente' ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                    {doc.EstadodeDocumento || 'Pendiente'}
                                                </span>
                                                <span className="ms-3 fw-bold text-secondary">{formatMoney(doc.ImporteTotal)}</span>
                                            </div>
                                        </div>
                                    </button>
                                </h2>

                                {isOpen && (
                                    <div className="accordion-collapse collapse show">
                                        <div className="accordion-body bg-light p-4">
                                            <div className="row">
                                                {/* STATUS DE FIRMAS */}
                                                <div className="col-md-5 border-end">
                                                    <h6 className="fw-bold text-primary mb-3"><i className="bi bi-pen me-2"></i>Status de Firmas</h6>
                                                    
                                                    {doc.firmas && doc.firmas.length > 0 ? (
                                                        <table className="table table-sm small bg-white border">
                                                            <thead className="table-secondary text-uppercase" style={{fontSize: '0.7rem'}}>
                                                                <tr>
                                                                    <th>Etapa</th>
                                                                    <th>Autorizado por</th>
                                                                    <th>Respuesta</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {doc.firmas.map((f, fIdx) => (
                                                                    <tr key={fIdx}>
                                                                        <td>{f.Etapa}</td>
                                                                        <td>{f.Aprobador}</td>
                                                                        <td>
                                                                            <span className={`fw-bold ${f.EstadoEtapa === 'Autorizado' ? 'text-success' : 'text-warning'}`}>
                                                                                {f.EstadoEtapa}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <p className="text-muted small">No hay etapas de firma configuradas.</p>
                                                    )}
                                                </div>

                                                {/* DETALLE DE ARTÍCULOS */}
                                                <div className="col-md-7">
                                                    <h6 className="fw-bold text-primary mb-3"><i className="bi bi-box-seam me-2"></i>Artículos</h6>
                                                    {detail ? (
                                                        <>
                                                            {detail.DocumentLines && detail.DocumentLines.length > 0 ? (
                                                                <table className="table table-sm small bg-white border">
                                                                    <thead className="table-secondary text-uppercase" style={{fontSize: '0.7rem'}}>
                                                                        <tr>
                                                                            <th>Código</th>
                                                                            <th>Descripción</th>
                                                                            <th className="text-center">Cant.</th>
                                                                            <th className="text-end">Total</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {detail.DocumentLines.map((line, lIdx) => (
                                                                            <tr key={lIdx}>
                                                                                <td>{line.ItemCode}</td>
                                                                                <td>{line.ItemDescription}</td>
                                                                                <td className="text-center">{line.Quantity}</td>
                                                                                <td className="text-end fw-bold">{formatMoney(line.LineTotal)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <p className="text-muted small">No se encontraron líneas de detalle en SAP.</p>
                                                            )}
                                                            <div className="small mt-2"><strong>Comentarios:</strong> {detail?.Comments || "Sin comentarios"}</div>
                                                        </>
                                                    ) : (
                                                        <div className="text-muted small">
                                                            <div className="spinner-border spinner-border-sm me-2 text-primary"></div>
                                                            Cargando artículos...
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TabStatusAutorizacion;